import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { API_URL } from "../../config.js";
import Notification from "../../components/Notification.js";
import { allowedToView } from "../../utils/PermissionUtils.js";

function PaymentPeriods() {
    const [paymentPeriods, setPaymentPeriods] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [notification, setNotification] = useState({ type: "", message: "" });

    // Pagination states
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const perPage = 20; // Items per page

    const userRole = localStorage.getItem("userRole");
    const canRunCalculation = (userRole) => {
        return (allowedToView(userRole, ['superadmin']));
    };
    const navigate = useNavigate();

    const [showModal, setShowModal] = useState(false);
    const [month, setMonth] = useState("");
    const [year, setYear] = useState(new Date().getFullYear());

    const [modalError, setModalError] = useState("");
    const fetchPaymentPeriods = useCallback(async () => {
        const token = localStorage.getItem("access_token");
        try {
            setLoading(true);

            // Construct the URL with the search parameter only if searchTerm is not empty
            const url = new URL(`${API_URL}/payment-periods`);
            url.searchParams.append("page", currentPage);
            if (searchTerm.trim()) {
                url.searchParams.append("search", searchTerm.trim());
            }

            const response = await fetch(url, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                },
            });

            if (response.ok) {
                const responseData = await response.json();
                console.log("Fetched Response:", responseData);

                if (responseData.data) {
                    setPaymentPeriods(responseData.data);
                    setTotalPages(responseData.last_page || 1);
                } else {
                    setPaymentPeriods([]);
                    setTotalPages(1);
                }
            } else if (response.status === 401) {
                navigate("/logout");
            } else {
                setError("Failed to fetch payment periods");
            }
        } catch (error) {
            setError("An error occurred while fetching payment periods.");
            console.error("Error:", error);
        } finally {
            setLoading(false);
        }
    }, [currentPage, searchTerm, navigate]);

    useEffect(() => {
        fetchPaymentPeriods();
    }, [currentPage, searchTerm, fetchPaymentPeriods]);

    const handleSearch = (event) => {
        setSearchTerm(event.target.value);
    };

    const handleSubmitCreate = async () => {
        if (!month || !year) {
            setModalError("Please select both month and year.");
            return;
        }

        try {
            const token = localStorage.getItem("access_token");
            const response = await fetch(`${API_URL}/payment-periods/create`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ month, year }),
            });

            const result = await response.json();

            if (response.ok) {
                setNotification({ type: "success", message: result.message || "Payment period created." });
                setShowModal(false);
                setModalError("");
                fetchPaymentPeriods();
            }
            else if (response.status === 422) {
                const errors = Object.values(result).flat().join(" ");
                setModalError(errors || "Validation failed. Please check your inputs.");
            }
            else if (response.status === 400) {
                setModalError(
                    result.message + (result.details ? " " + result.details : "") ||
                    "Unable to create payment period. Please try again."
                );
            }
            else if (response.status === 404) {
                setNotification({ type: "danger", message: "Not found." });
            }
            else if (response.status === 401) {
                navigate("/logout");
            }
            else {
                setModalError(result.message || "Something went wrong.");
            }
        } catch (error) {
            console.error("Create error:", error);
            setModalError(error.message || "An unexpected error occurred.");
        }
    };

    return (
        <div>
            <main>
                <nav className="level">
                    <div className="level-left">
                        <p className="title">Payment Periods </p>
                    </div>
                    <div className="level-right">
                        {canRunCalculation(userRole) && (
                            <button className="button is-primary" onClick={() => setShowModal(true)}>
                                Create New
                            </button>
                        )}

                    </div>
                </nav>

                <h1>Payment Periods List</h1>

                {notification.message && (
                    <Notification type={notification.type} message={notification.message} />
                )}

                <input
                    className="input"
                    type="text"
                    placeholder="Search payment periods..."
                    value={searchTerm}
                    onChange={handleSearch}
                />

                {loading ? (
                    <p>Loading payment periods...</p>
                ) : error ? (
                    <p className="has-text-danger">{error}</p>
                ) : (
                    <table className="table is-striped is-fullwidth">
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>Name</th>
                                <th>Start Date</th>
                                <th>End Date</th>
                                <th>Status</th>
                                <th>Options</th>
                            </tr>
                        </thead>
                        <tbody>
                            {paymentPeriods?.length > 0 ? (
                                paymentPeriods.map((period, index) => (
                                    <tr key={period.id}>
                                        <td>{index + 1 + (currentPage - 1) * perPage}</td>
                                        <td>
                                            <Link to={`${period.id}/summary`} className="no_deco_link">
                                                {period.name}
                                            </Link>
                                        </td>
                                        <td>{new Date(period.start_date).toLocaleDateString()}</td>
                                        <td>{new Date(period.end_date).toLocaleDateString()}</td>
                                        <td>
                                            {period.is_closed ? (
                                                <span className="tag is-danger">Closed</span>
                                            ) : (
                                                <span className="tag is-success">Open</span>
                                            )}
                                        </td>
                                        <td>
                                            <Link to={`${period.id}/summary`} className="no_deco_link">
                                                <span className="icon">
                                                    <i className="fas fa-eye"></i>
                                                </span>
                                                <span>View</span>
                                            </Link>

                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="6" className="has-text-centered">No payment periods found</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                )}

                {/* Pagination Controls */}
                <nav className="pagination is-centered">
                    <button
                        className="pagination-previous"
                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                    >
                        Previous
                    </button>
                    <span className="pagination-page">Page {currentPage} of {totalPages}</span>
                    <button
                        className="pagination-next"
                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                        disabled={currentPage === totalPages}
                    >
                        Next
                    </button>
                </nav>

                {showModal && (
                    <div className="modal is-active">
                        <div className="modal-background" onClick={() => setShowModal(false)}></div>
                        <div className="modal-card">
                            <header className="modal-card-head">
                                <p className="modal-card-title">Create New Payment Period</p>
                                <button className="delete" onClick={() => setShowModal(false)}></button>
                            </header>
                            <section className="modal-card-body">
                                {modalError && <p className="has-text-danger mb-3">{modalError}</p>}

                                <div className="field">
                                    <label className="label">Month</label>
                                    <div className="control">
                                        <div className="select">
                                            <select value={month} onChange={(e) => setMonth(e.target.value)}>
                                                <option value="">Select month</option>
                                                {Array.from({ length: 12 }, (_, i) => (
                                                    <option key={i + 1} value={i + 1}>
                                                        {new Date(0, i).toLocaleString("default", { month: "long" })}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                </div>

                                <div className="field">
                                    <label className="label">Year</label>
                                    <div className="control">
                                        <input
                                            className="input"
                                            type="number"
                                            value={year}
                                            onChange={(e) => setYear(e.target.value)}
                                        />
                                    </div>
                                </div>
                            </section>
                            <footer className="modal-card-foot">
                                <button className="button is-success" onClick={handleSubmitCreate}>
                                    Submit
                                </button>
                                <button className="button" onClick={() => setShowModal(false)}>
                                    Cancel
                                </button>
                            </footer>
                        </div>
                    </div>
                )}

            </main>
        </div>
    );
}

export default PaymentPeriods;